import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './work-order.reducer';

export const WorkOrder = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const workOrderList = useAppSelector(state => state.workOrder.entities);
  const loading = useAppSelector(state => state.workOrder.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="work-order-heading" data-cy="WorkOrderHeading">
        <Translate contentKey="crawlerBackendApp.workOrder.home.title">Work Orders</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.workOrder.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/work-order/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.workOrder.home.createLabel">Create new Work Order</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {workOrderList && workOrderList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.workOrder.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('startDate')}>
                  <Translate contentKey="crawlerBackendApp.workOrder.startDate">Start Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('startDate')} />
                </th>
                <th className="hand" onClick={sort('endDate')}>
                  <Translate contentKey="crawlerBackendApp.workOrder.endDate">End Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('endDate')} />
                </th>
                <th className="hand" onClick={sort('workStatus')}>
                  <Translate contentKey="crawlerBackendApp.workOrder.workStatus">Work Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('workStatus')} />
                </th>
                <th className="hand" onClick={sort('description')}>
                  <Translate contentKey="crawlerBackendApp.workOrder.description">Description</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('description')} />
                </th>
                <th className="hand" onClick={sort('createdTimestamp')}>
                  <Translate contentKey="crawlerBackendApp.workOrder.createdTimestamp">Created Timestamp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdTimestamp')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.workOrder.serviceOrder">Service Order</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.workOrder.owner">Owner</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {workOrderList.map((workOrder, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/work-order/${workOrder.id}`} color="link" size="sm">
                      {workOrder.id}
                    </Button>
                  </td>
                  <td>
                    {workOrder.startDate ? (
                      <TextFormat type="date" value={workOrder.startDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {workOrder.endDate ? (
                      <TextFormat type="date" value={workOrder.endDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.WorkStatus.${workOrder.workStatus}`} />
                  </td>
                  <td>{workOrder.description}</td>
                  <td>
                    {workOrder.createdTimestamp ? (
                      <TextFormat type="date" value={workOrder.createdTimestamp} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {workOrder.serviceOrder ? (
                      <Link to={`/service-order/${workOrder.serviceOrder.id}`}>{workOrder.serviceOrder.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {workOrder.owner ? <Link to={`/employee/${workOrder.owner.id}`}>{workOrder.owner.id}</Link> : ''}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/work-order/${workOrder.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/work-order/${workOrder.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/work-order/${workOrder.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.workOrder.home.notFound">No Work Orders found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default WorkOrder;
